import CircularProgress from '@material-ui/core/CircularProgress'
import React, { memo, useCallback, useMemo } from 'react'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import Add from '@material-ui/icons/Add'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { DataNode } from 'components/WorkspaceEditor/types'
import { usePTCreationPermission } from './hooks/usePTCreationPermission'

/** Props for rendering add icon of <TreeView /> component */
export interface AddIconProps {
    /** Node to add */
    node: DataNode
    /** Function to add a new tree node */
    onAdd?: (node: DataNode) => void
    /** A flag whether the node is loading */
    isLoading: boolean
    /** Test id for the add icon */
    'data-testid'?: string
    /** Whether the add functionality is disabled */
    isDisabled?: boolean
}

/**
 * Renders the add icon property of <TreeView /> component
 * @param {AddIconProps} props - Props for rendering add icon
 * @returns {ReactElement} Node add icon
 */
export const AddIcon = memo<AddIconProps>(
    ({
        node,
        onAdd,
        isLoading = false,
        'data-testid': dataTestId = 'add-icon',
        isDisabled = false,
    }) => {
        const styles = useTreeViewStyles()
        const ptCreation = usePTCreationPermission()
        const isNodeInProject = useShallowSelector(
            (state) => state.selectedProjectElementNode?.isInProject
        )

        const isAddDisabled = useMemo(
            () =>
                isDisabled ||
                node.isHidden ||
                !isNodeInProject ||
                !ptCreation.isAllowed,
            [isDisabled, node.isHidden, isNodeInProject, ptCreation.isAllowed]
        )

        const handleClick = useCallback(() => {
            if (isAddDisabled) {
                if (!ptCreation.isAllowed) {
                    ptCreation.showRestrictionNotification()
                }
                return
            }

            onAdd?.(node)
        }, [isAddDisabled, ptCreation, onAdd, node])

        if (isLoading) {
            return (
                <CircularProgress
                    data-testid={dataTestId}
                    className={styles.switcherLoadingIcon}
                    size={16}
                />
            )
        }

        return (
            <HoverTooltip
                text={
                    isAddDisabled
                        ? 'Adding content is not available'
                        : 'Add Content'
                }
                className={
                    isAddDisabled ? styles.disabledAddIcon : styles.addIcon
                }
                placements={['bottom']}
            >
                <Add
                    data-testid="addButton"
                    fontSize="inherit"
                    onClick={handleClick}
                />
            </HoverTooltip>
        )
    }
)
